/**
 * ReporteRubros.js — Matriz Filial × Rubro
 *
 * Una fila por filial, una columna por rubro (cuenta de ingreso), y el total
 * de la filial al final:
 *
 *    Filial │  Cuotas  │ Multas │ Otros  │  Total
 *    A01    │ 1,250.10 │ 686.20 │ 325.00 │ 2,261.30
 *
 * Selector Facturado / Cobrado / Pendiente — los tres sobre el mismo cuadro,
 * porque el contador necesita tanto la composición de lo emitido como la de lo
 * que entró y la de lo que falta.
 *
 * ── Sobre la presentación ────────────────────────────────────────────────────
 * Una matriz cruda no se lee: con ocho rubros y cincuenta filiales son
 * cuatrocientas cifras del mismo peso visual y no se distingue lo grande de lo
 * chico. Por eso, antes de la tabla van el resumen y la composición por rubro
 * — que es la pregunta que este reporte contesta — y dentro de la tabla cada
 * celda lleva un tinte proporcional a su magnitud DENTRO DE SU COLUMNA, así el
 * ojo encuentra los pesos pesados sin tener que leer número por número.
 *
 * Las columnas son dinámicas: aparecen solo los rubros con movimiento en el
 * período, así que agregar una cuenta de ingreso nueva (Multas, Cobros
 * judiciales) la hace aparecer sola, sin tocar código.
 *
 * Montos del backend en COLONES; el toggle ₡/$ convierte con el TC de compra,
 * igual que Informes y los listados.
 */
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { useTipoCambio } from "../../services/useTipoCambio";
import { useLang } from "../../../../shared/i18n/useLang";
import { useEsCliente } from "../../../../shared/hooks/useTenantTipo";
import { useTheme } from "../../../../shared/contexts/ThemeContext";
import { downloadExcel } from "../../shared/excelService";
import CurrencyToggle from "../../shared/CurrencyToggle";
import invoiceStyles from "../../invoices/styles/invoice.module.css";

/* Misma paleta que la dona por rubro de Informes: un rubro debería verse del
   mismo color en los dos reportes. Validada para que cada hue se distinga de
   sus vecinos, también bajo daltonismo, en tema claro y oscuro. */
const RUBRO_COLORS = {
  dark:  ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"],
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
};
const RUBRO_OTROS = { dark: "#6b7280", light: "#94a3b8" };

/* Cada modo tiene su color: el resumen y los acentos cambian con él, para que
   se note de un vistazo si lo que estás viendo es emitido, cobrado o deuda. */
const MODO_COLOR = { facturado: "#f97316", cobrado: "#22c55e", pendiente: "#f59e0b" };

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function rangoMes(offset = 0) {
  const n = new Date();
  return [
    ymd(new Date(n.getFullYear(), n.getMonth() + offset, 1)),
    ymd(new Date(n.getFullYear(), n.getMonth() + offset + 1, 0)),
  ];
}
function rangoAnio() {
  const y = new Date().getFullYear();
  return [`${y}-01-01`, `${y}-12-31`];
}

function tinte(hex, alpha) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const enlaceStyle = {
  background: "none", border: "none", padding: "4px 2px", cursor: "pointer",
  fontSize: "0.76rem", fontWeight: 600, color: "var(--text-muted)",
  textDecoration: "underline", textUnderlineOffset: 3,
};

function useNarrow(bp = 880) {
  const [n, setN] = useState(() => (typeof window !== "undefined" ? window.innerWidth < bp : false));
  useEffect(() => {
    const h = () => setN(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return n;
}

export default function ReporteRubros() {
  const L = useLang();
  const esCliente = useEsCliente();
  const multimoneda = useMultimoneda();
  const { tipoCambio } = useTipoCambio();
  const { theme } = useTheme();
  const narrow = useNarrow(880);
  const tc = Number(tipoCambio?.tipo_compra) || 1;

  const [aI, aF] = rangoAnio();
  const [modo, setModo] = useState("facturado");
  const [vista, setVista] = useState("CRC");
  const [desde, setDesde] = useState(aI);
  const [hasta, setHasta] = useState(aF);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bajando, setBajando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState({ key: "total", dir: "desc" });
  // Rubros: se guardan los OCULTOS (vacío = se ven todos).
  // Filiales: se guardan las ELEGIDAS (vacío = se ven todas).
  const [filtroRubros, setFiltroRubros] = useState(() => new Set());
  const [filtroFiliales, setFiltroFiliales] = useState(() => new Set());
  const [panelFiliales, setPanelFiliales] = useState(false);

  // Al cambiar de modo o de período cambian los rubros y las filiales con
  // movimiento: una selección vieja escondería datos nuevos sin avisar.
  useEffect(() => {
    setFiltroRubros(new Set());
    setFiltroFiliales(new Set());
  }, [modo, desde, hasta]);

  const entidad = esCliente ? L("Cliente", "Client") : L("Filial", "Unit");
  const esUSD = vista === "USD";
  const sym = esUSD ? "$" : "₡";
  const acento = MODO_COLOR[modo];
  const paleta = RUBRO_COLORS[theme] ?? RUBRO_COLORS.dark;
  const gris = RUBRO_OTROS[theme] ?? RUBRO_OTROS.dark;

  const conv = (v) => Number(v || 0) / (esUSD ? tc : 1);
  const fmt = (v) => `${sym}${formatCurrency(conv(v))}`;
  /* Los totales grandes del resumen no necesitan los centavos: ocupan lugar y
     empujan la cifra a un tamaño ilegible en pantallas angostas. */
  const fmtCorto = (v) => {
    const n = conv(v);
    if (Math.abs(n) >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 10_000)    return `${sym}${Math.round(n).toLocaleString("es-CR")}`;
    return `${sym}${formatCurrency(n)}`;
  };

  const qs = useMemo(() => {
    const p = new URLSearchParams({ modo });
    if (desde) p.set("desde", desde);
    if (hasta) p.set("hasta", hasta);
    return p.toString();
  }, [modo, desde, hasta]);

  useEffect(() => {
    setLoading(true);
    setError("");
    apiFetch(`/api/finance/reportes/rubros-filial/?${qs}`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {
        setData(null);
        setError(L("No se pudo cargar el reporte.", "Could not load the report."));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  async function exportar() {
    setBajando(true);
    try {
      /* El Excel baja lo que estás viendo, no el cuadro completo: si no, el
         archivo contradiría la pantalla. Las filiales se mandan resueltas —
         las que quedaron tras la selección Y la búsqueda — para no tener que
         reimplementar el buscador en el backend. */
      const p = new URLSearchParams(qs);
      if (rubrosOcultos.size > 0) {
        p.set("rubros", rubrosView.map((r) => r.id).join(","));
      }
      if (filialesElegidas.size > 0 || busqueda.trim()) {
        p.set("filiales", filasCalc.map((f) => f.id).join(","));
      }
      await downloadExcel(
        `/api/finance/reportes/rubros-filial-excel/?${p}`,
        `rubros_por_filial_${modo}.xlsx`,
      );
    } catch {
      setError(L("No se pudo generar el Excel.", "Could not generate the Excel file."));
    } finally {
      setBajando(false);
    }
  }

  const periodoActivo = (() => {
    const [m0i, m0f] = rangoMes(0);
    const [m1i, m1f] = rangoMes(-1);
    if (desde === m0i && hasta === m0f) return "mes";
    if (desde === m1i && hasta === m1f) return "mesPasado";
    if (desde === aI && hasta === aF) return "anio";
    if (!desde && !hasta) return "todo";
    return null;
  })();

  const MODOS = [
    ["facturado", L("Facturado", "Billed"), L("Lo emitido en el período", "Issued during the period")],
    ["cobrado", L("Cobrado", "Collected"), L("Lo que entró, por fecha de pago", "Received, by payment date")],
    ["pendiente", L("Pendiente", "Outstanding"), L("Saldo actual de esas facturas", "Current balance of those invoices")],
  ];
  const ayudaModo = MODOS.find(([v]) => v === modo)?.[2] ?? "";

  const rubros = useMemo(() => data?.rubros ?? [], [data]);
  const filas = useMemo(() => data?.filas ?? [], [data]);
  const hayDatos = filas.length > 0;

  /* ── Filtros ────────────────────────────────────────────────────────────
     Los dos conjuntos vacíos significan "sin filtrar". Guardar la selección
     como "todo lo que NO está excluido" haría que un rubro nuevo apareciera
     desmarcado; así, si mañana se factura un rubro que no existía, entra solo. */
  const rubrosOcultos  = filtroRubros;
  const filialesElegidas = filtroFiliales;

  /* Orden y color de los rubros: se calculan SIEMPRE sobre el total sin
     filtrar, para que un rubro conserve su color aunque se oculten otros. Si
     el color dependiera de la selección, cada clic repintaría toda la página. */
  const rubrosBase = useMemo(() => {
    if (!data) return [];
    return rubros
      .map((r) => ({ ...r, montoFull: data.totales?.[String(r.id)] ?? 0 }))
      .sort((a, b) => b.montoFull - a.montoFull)
      .map((r, i) => ({ ...r, color: i < paleta.length ? paleta[i] : gris }));
  }, [data, rubros, paleta, gris]);

  const rubrosView = useMemo(
    () => rubrosBase.filter((r) => !rubrosOcultos.has(r.id)),
    [rubrosBase, rubrosOcultos]);

  /* Con rubros ocultos, los totales del backend dejan de servir: incluyen
     columnas que ya no se ven. Se recalcula todo sobre lo visible para que la
     suma de la pantalla sea la suma de lo que la pantalla muestra. */
  const filasCalc = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return filas
      .filter((f) => (filialesElegidas.size === 0 || filialesElegidas.has(f.id)))
      .filter((f) => !q
        || (f.nombre ?? "").toLowerCase().includes(q)
        || (f.propietario ?? "").toLowerCase().includes(q))
      .map((f) => ({
        ...f,
        totalView: rubrosView.reduce(
          (s, r) => s + (f.valores?.[String(r.id)] ?? 0), 0),
      }))
      // Una filial cuyos únicos rubros quedaron ocultos aportaría una fila
      // entera de guiones sumando cero — no aporta nada, se saca.
      .filter((f) => f.totalView !== 0);
  }, [filas, busqueda, filialesElegidas, rubrosView]);

  const totalesView = useMemo(() => {
    const m = {};
    rubrosView.forEach((r) => {
      m[r.id] = filasCalc.reduce((s, f) => s + (f.valores?.[String(r.id)] ?? 0), 0);
    });
    return m;
  }, [rubrosView, filasCalc]);

  const totalGeneral = useMemo(
    () => Object.values(totalesView).reduce((s, v) => s + v, 0), [totalesView]);

  /* Rubros visibles con su monto y su peso relativo — alimenta la barra de
     composición, la leyenda y las columnas de la tabla, para que las tres
     cuenten la misma historia. */
  const composicion = useMemo(
    () => rubrosView.map((r) => ({
      ...r,
      monto: totalesView[r.id] ?? 0,
      pct: totalGeneral ? ((totalesView[r.id] ?? 0) / totalGeneral) * 100 : 0,
    })),
    [rubrosView, totalesView, totalGeneral]);

  /* Máximo por columna: la base del tinte de cada celda. Se compara dentro del
     rubro y no contra el total, porque si no, un rubro chico queda plano y no
     se ve quién destaca dentro de él. */
  const maxPorRubro = useMemo(() => {
    const m = {};
    rubrosView.forEach((r) => {
      m[r.id] = filasCalc.reduce((mx, f) => Math.max(mx, f.valores?.[String(r.id)] ?? 0), 0);
    });
    return m;
  }, [rubrosView, filasCalc]);
  const maxTotal = useMemo(
    () => filasCalc.reduce((mx, f) => Math.max(mx, f.totalView ?? 0), 0), [filasCalc]);

  const filasVisibles = useMemo(() => {
    const valor = (f) => (orden.key === "total"
      ? (f.totalView ?? 0)
      : (f.valores?.[String(orden.key)] ?? 0));
    const copia = [...filasCalc];
    copia.sort((a, b) => {
      if (orden.key === "nombre") {
        const r = (a.nombre ?? "").localeCompare(b.nombre ?? "");
        return orden.dir === "asc" ? r : -r;
      }
      const r = valor(a) - valor(b);
      return orden.dir === "asc" ? r : -r;
    });
    return copia;
  }, [filasCalc, orden]);

  function ordenarPor(key) {
    setOrden((o) => o.key === key
      ? { key, dir: o.dir === "desc" ? "asc" : "desc" }
      : { key, dir: key === "nombre" ? "asc" : "desc" });
  }

  function toggleRubro(id) {
    setFiltroRubros((s) => {
      const n = new Set(s);
      // Ocultar el último rubro visible dejaría la tabla sin columnas y todos
      // los totales en cero: se ignora el clic.
      if (!n.has(id) && rubrosBase.length - n.size <= 1) return n;
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleFilial(id) {
    setFiltroFiliales((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function limpiarFiltros() {
    setFiltroRubros(new Set());
    setFiltroFiliales(new Set());
    setBusqueda("");
  }
  const hayFiltros = rubrosOcultos.size > 0 || filialesElegidas.size > 0 || busqueda.trim() !== "";

  const promedio = filasCalc.length ? totalGeneral / filasCalc.length : 0;

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/"><span className={invoiceStyles.inactive}>{L("Inicio", "Home")} /</span></Link>
        <span className={invoiceStyles.active}> {L("Reporte por Rubro", "Report by Line Item")}</span>
      </span>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">
            {L(`Reporte por ${entidad} y Rubro`, `Report by ${entidad} and Line Item`)}
          </h2>
          <p className="mb-0" style={{ color: "var(--text-muted)" }}>
            {L(`Cada columna es una cuenta de ingreso; cada fila, una ${entidad.toLowerCase()}`,
               `Each column is an income account; each row, one ${entidad.toLowerCase()}`)}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          {multimoneda && (
            <CurrencyToggle value={vista} onChange={setVista}
              title={L("Moneda de visualización", "Display currency")} />
          )}
          <button type="button" onClick={exportar} disabled={bajando || !hayDatos}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px",
              borderRadius: 8, border: "1px solid rgba(34,197,94,0.4)",
              background: "rgba(34,197,94,0.15)", color: "#22c55e",
              fontWeight: 600, fontSize: "0.83rem",
              cursor: bajando || !hayDatos ? "not-allowed" : "pointer",
              opacity: bajando || !hayDatos ? 0.55 : 1,
            }}>
            <i className={`fa-solid ${bajando ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
            {L("Exportar Excel", "Export Excel")}
          </button>
        </div>
      </div>

      {/* ── Controles: modo + período en una sola barra ──────────────────
          Antes el modo ocupaba tres tarjetas grandes y el período otra barra:
          media pantalla de controles antes de ver un solo número. */}
      <Card style={{ marginBottom: 14, padding: "12px 14px" }}>
        <div style={{
          display: "flex", gap: 12, flexWrap: "wrap",
          alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", background: "var(--bg)", borderRadius: 10, padding: 3, gap: 3 }}>
            {MODOS.map(([v, lbl]) => (
              <button key={v} type="button" onClick={() => setModo(v)}
                style={{
                  padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontSize: "0.82rem", fontWeight: 700, transition: "all 0.15s",
                  background: modo === v ? tinte(MODO_COLOR[v], 0.16) : "transparent",
                  color: modo === v ? MODO_COLOR[v] : "var(--text-muted)",
                  boxShadow: modo === v ? `inset 0 0 0 1px ${tinte(MODO_COLOR[v], 0.4)}` : "none",
                }}>
                {lbl}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <PeriodBtn active={periodoActivo === "mes"} onClick={() => { const [i, f] = rangoMes(0); setDesde(i); setHasta(f); }}>
              {L("Este mes", "This month")}
            </PeriodBtn>
            <PeriodBtn active={periodoActivo === "mesPasado"} onClick={() => { const [i, f] = rangoMes(-1); setDesde(i); setHasta(f); }}>
              {L("Mes pasado", "Last month")}
            </PeriodBtn>
            <PeriodBtn active={periodoActivo === "anio"} onClick={() => { setDesde(aI); setHasta(aF); }}>
              {L("Este año", "This year")}
            </PeriodBtn>
            <PeriodBtn active={periodoActivo === "todo"} onClick={() => { setDesde(""); setHasta(""); }}>
              {L("Todo", "All time")}
            </PeriodBtn>
            <FechaInput label={L("Desde", "From")} value={desde} onChange={setDesde} />
            <FechaInput label={L("Hasta", "To")} value={hasta} onChange={setHasta} />
          </div>
        </div>
        <div style={{ marginTop: 9, fontSize: "0.75rem", color: "var(--text-muted)" }}>
          <i className="fa-solid fa-circle-info" style={{ marginRight: 6, opacity: 0.7 }} />
          {ayudaModo}
        </div>
      </Card>

      {/* ── Filtros ──────────────────────────────────────────────────────
          Los rubros van como chips: son pocos y con su color puesto se
          reconocen sin leer. Las filiales pueden ser cientos, así que van en
          un panel que se abre, con buscador propio. */}
      {hayDatos && (
        <Card style={{ marginBottom: 14, padding: "12px 14px" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{
              fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.5px",
              textTransform: "uppercase", color: "var(--text-muted)", marginRight: 2,
            }}>
              {L("Rubros", "Line items")}
            </span>
            {rubrosBase.map((r) => {
              const on = !rubrosOcultos.has(r.id);
              return (
                <button key={r.id} type="button" onClick={() => toggleRubro(r.id)}
                  title={r.codigo}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "5px 11px", borderRadius: 999, cursor: "pointer",
                    fontSize: "0.78rem", fontWeight: 600, maxWidth: 220,
                    border: `1px solid ${on ? tinte(r.color, 0.55) : "var(--border-card)"}`,
                    background: on ? tinte(r.color, 0.14) : "transparent",
                    color: on ? "var(--text-primary)" : "var(--text-muted)",
                    opacity: on ? 1 : 0.6, transition: "all 0.15s",
                  }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                    background: on ? r.color : "var(--text-muted)",
                  }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.nombre}
                  </span>
                </button>
              );
            })}
            {rubrosOcultos.size > 0 && (
              <button type="button" onClick={() => setFiltroRubros(new Set())}
                style={enlaceStyle}>
                {L("Ver todos", "Show all")}
              </button>
            )}
          </div>

          <div style={{
            display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
            marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border-card)",
          }}>
            <span style={{
              fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.5px",
              textTransform: "uppercase", color: "var(--text-muted)",
            }}>
              {entidad}
            </span>
            <button type="button" onClick={() => setPanelFiliales((v) => !v)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "5px 12px", borderRadius: 8, cursor: "pointer",
                fontSize: "0.78rem", fontWeight: 600,
                border: `1px solid ${filialesElegidas.size ? tinte(acento, 0.5) : "var(--border-card)"}`,
                background: filialesElegidas.size ? tinte(acento, 0.12) : "transparent",
                color: filialesElegidas.size ? acento : "var(--text-secondary)",
              }}>
              <i className="fa-solid fa-filter" style={{ fontSize: "0.7rem" }} />
              {filialesElegidas.size
                ? L(`${filialesElegidas.size} seleccionada(s)`, `${filialesElegidas.size} selected`)
                : L("Todas", "All")}
              <i className={`fa-solid fa-chevron-${panelFiliales ? "up" : "down"}`}
                style={{ fontSize: "0.62rem", opacity: 0.7 }} />
            </button>
            {hayFiltros && (
              <button type="button" onClick={limpiarFiltros} style={enlaceStyle}>
                <i className="fa-solid fa-xmark" style={{ marginRight: 5 }} />
                {L("Limpiar filtros", "Clear filters")}
              </button>
            )}
            <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "var(--text-muted)" }}>
              {L(`Mostrando ${filasCalc.length} de ${filas.length}`,
                 `Showing ${filasCalc.length} of ${filas.length}`)}
              {" · "}
              {L(`${rubrosView.length} de ${rubrosBase.length} rubros`,
                 `${rubrosView.length} of ${rubrosBase.length} line items`)}
            </span>
          </div>

          {panelFiliales && (
            <div style={{
              marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border-card)",
              maxHeight: 210, overflowY: "auto",
              display: "grid", gap: 4,
              gridTemplateColumns: narrow ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))",
            }}>
              {filas.map((f) => {
                const on = filialesElegidas.has(f.id);
                return (
                  <label key={f.id} style={{
                    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                    padding: "5px 8px", borderRadius: 7, fontSize: "0.8rem",
                    background: on ? tinte(acento, 0.1) : "transparent",
                    color: on ? "var(--text-primary)" : "var(--text-secondary)",
                  }}>
                    <input type="checkbox" checked={on} onChange={() => toggleFilial(f.id)}
                      style={{ accentColor: acento, flexShrink: 0 }} />
                    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.nombre}
                      {f.propietario && (
                        <span style={{ color: "var(--text-muted)" }}> · {f.propietario}</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {error && (
        <div style={{
          background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)",
          color: "#ef4444", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: "0.85rem",
        }}>{error}</div>
      )}

      {loading ? (
        <Card><Vacio texto={L("Cargando…", "Loading…")} icono="fa-spinner fa-spin" /></Card>
      ) : !hayDatos ? (
        <Card>
          <Vacio icono="fa-table-cells"
            texto={L("No hay movimientos en el período seleccionado.",
                     "No activity in the selected period.")} />
        </Card>
      ) : (
        <>
          {/* ── Resumen ──────────────────────────────────────────────── */}
          <div style={{
            display: "grid", gap: 12, marginBottom: 14,
            gridTemplateColumns: narrow ? "repeat(auto-fit, minmax(150px, 1fr))" : "repeat(4, 1fr)",
          }}>
            <KPI etiqueta={MODOS.find(([v]) => v === modo)?.[1]} valor={fmtCorto(totalGeneral)}
              color={acento} icono="fa-coins" destacado />
            <KPI etiqueta={L("Rubro principal", "Top line item")}
              valor={composicion[0]?.nombre ?? "—"}
              nota={composicion[0] ? `${composicion[0].pct.toFixed(1)}% · ${fmtCorto(composicion[0].monto)}` : null}
              color={composicion[0]?.color ?? gris} icono="fa-trophy" />
            <KPI etiqueta={L(`${entidad}es con movimiento`, `${entidad}s with activity`)}
              valor={String(filas.length)} color="#60a5fa" icono="fa-building" />
            <KPI etiqueta={L(`Promedio por ${entidad.toLowerCase()}`, `Average per ${entidad.toLowerCase()}`)}
              valor={fmtCorto(promedio)} color="#a78bfa" icono="fa-chart-simple" />
          </div>

          {/* ── Composición por rubro ────────────────────────────────── */}
          <Card style={{ marginBottom: 14 }}>
            <CardTitle icon="fa-layer-group" color={acento}
              title={L("Composición por rubro", "Breakdown by line item")} />
            <div style={{
              display: "flex", height: 14, borderRadius: 7, overflow: "hidden",
              background: "var(--bg)", marginBottom: 14,
            }}>
              {composicion.filter((r) => r.pct > 0).map((r) => (
                <div key={r.id} title={`${r.nombre} — ${r.pct.toFixed(1)}%`}
                  style={{ width: `${r.pct}%`, background: r.color, transition: "width 0.25s" }} />
              ))}
            </div>
            <div style={{
              display: "grid", gap: "8px 20px",
              gridTemplateColumns: narrow ? "1fr" : "repeat(auto-fit, minmax(260px, 1fr))",
            }}>
              {composicion.map((r) => (
                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: r.color, flexShrink: 0 }} />
                  <span title={r.codigo} style={{
                    fontSize: "0.82rem", color: "var(--text-secondary)", flex: 1,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {r.nombre}
                  </span>
                  <span style={{
                    fontSize: "0.82rem", fontWeight: 700, color: "var(--text-primary)",
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {fmt(r.monto)}
                  </span>
                  <span style={{
                    fontSize: "0.75rem", color: "var(--text-muted)", minWidth: 46, textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {r.pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* ── Detalle ──────────────────────────────────────────────── */}
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 10, flexWrap: "wrap", padding: "13px 16px",
              borderBottom: "1px solid var(--border-card)",
            }}>
              <span style={{
                fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.6px",
                textTransform: "uppercase", color: "var(--text-muted)",
              }}>
                {L("Detalle por ", "Detail by ")}{entidad.toLowerCase()}
                <span style={{ marginLeft: 8, color: "var(--text-secondary)", textTransform: "none", letterSpacing: 0 }}>
                  {filasVisibles.length !== filas.length
                    ? `${filasVisibles.length} / ${filas.length}`
                    : filas.length}
                </span>
              </span>
              <div style={{ position: "relative" }}>
                <i className="fa-solid fa-magnifying-glass" style={{
                  position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                  fontSize: "0.72rem", color: "var(--text-muted)", pointerEvents: "none",
                }} />
                <input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder={L(`Buscar ${entidad.toLowerCase()} o propietario…`,
                                 `Search ${entidad.toLowerCase()} or owner…`)}
                  style={{
                    background: "var(--bg)", border: "1px solid var(--border-card)",
                    borderRadius: 8, color: "var(--text-secondary)",
                    padding: "6px 10px 6px 30px", fontSize: "0.8rem",
                    width: narrow ? 180 : 250, outline: "none",
                  }} />
              </div>
            </div>

            {filasVisibles.length === 0 ? (
              <Vacio icono="fa-magnifying-glass"
                texto={L("Ninguna coincide con la búsqueda.", "Nothing matches your search.")} />
            ) : narrow ? (
              /* En angosto la matriz no cabe: una tarjeta por filial con su
                 desglose apilado se lee, una tabla de 10 columnas no. */
              <div style={{ padding: 12, display: "grid", gap: 10 }}>
                {filasVisibles.map((f) => (
                  <div key={f.id} style={{
                    border: "1px solid var(--border-card)", borderRadius: 10, padding: "11px 13px",
                    background: "var(--bg)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "0.9rem" }}>{f.nombre}</div>
                        {f.propietario && (
                          <div style={{
                            fontSize: "0.73rem", color: "var(--text-muted)",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>{f.propietario}</div>
                        )}
                      </div>
                      <div style={{
                        fontWeight: 700, color: acento, fontSize: "0.95rem",
                        fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                      }}>{fmt(f.totalView)}</div>
                    </div>
                    <div style={{ marginTop: 9, display: "grid", gap: 5 }}>
                      {composicion.filter((r) => f.valores?.[String(r.id)]).map((r) => (
                        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                          <span style={{
                            fontSize: "0.78rem", color: "var(--text-muted)", flex: 1,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>{r.nombre}</span>
                          <span style={{
                            fontSize: "0.79rem", color: "var(--text-secondary)",
                            fontVariantNumeric: "tabular-nums",
                          }}>{fmt(f.valores[String(r.id)])}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{
                  display: "flex", justifyContent: "space-between", padding: "11px 13px",
                  borderRadius: 10, background: tinte(acento, 0.1),
                  border: `1px solid ${tinte(acento, 0.3)}`,
                }}>
                  <span style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "0.85rem" }}>
                    {L("TOTAL", "TOTAL")}
                  </span>
                  <span style={{ fontWeight: 700, color: acento, fontVariantNumeric: "tabular-nums" }}>
                    {fmt(totalGeneral)}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ overflow: "auto", maxHeight: "min(68vh, 640px)" }}>
                <table style={{
                  width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 560,
                }}>
                  <thead>
                    <tr>
                      <Th first onClick={() => ordenarPor("nombre")} orden={orden} col="nombre">
                        {entidad}
                      </Th>
                      {composicion.map((r) => (
                        <Th key={r.id} align="right" title={`${r.codigo} — ${r.nombre}`}
                          onClick={() => ordenarPor(r.id)} orden={orden} col={r.id} dot={r.color}>
                          {r.nombre}
                        </Th>
                      ))}
                      <Th align="right" strong color={acento}
                        onClick={() => ordenarPor("total")} orden={orden} col="total">
                        {L("Total", "Total")}
                      </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasVisibles.map((f) => (
                      <tr key={f.id} className="rubro-row">
                        <Td first>
                          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{f.nombre}</div>
                          {f.propietario && (
                            <div style={{ fontSize: "0.73rem", color: "var(--text-muted)" }}>{f.propietario}</div>
                          )}
                        </Td>
                        {composicion.map((r) => {
                          const v = f.valores?.[String(r.id)];
                          const mx = maxPorRubro[r.id] || 0;
                          return (
                            <Td key={r.id} align="right" muted={!v}
                              bg={v && mx ? tinte(r.color, 0.05 + (v / mx) * 0.19) : undefined}>
                              {v ? fmt(v) : "–"}
                            </Td>
                          );
                        })}
                        <Td align="right" strong
                          bg={maxTotal ? tinte(acento, 0.04 + (f.totalView / maxTotal) * 0.16) : undefined}>
                          {fmt(f.totalView)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <Td first footer strong>{L("TOTAL", "TOTAL")}</Td>
                      {composicion.map((r) => (
                        <Td key={r.id} align="right" footer strong>{fmt(r.monto)}</Td>
                      ))}
                      <Td align="right" footer strong color={acento}>{fmt(totalGeneral)}</Td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {hayDatos && modo === "pendiente" && (
        <p style={{ marginTop: 10, fontSize: "0.76rem", color: "var(--text-muted)", fontStyle: "italic" }}>
          {L("El pendiente es el saldo de HOY de las facturas emitidas en el período, no el saldo que había a la fecha de corte.",
             "Outstanding shows TODAY's balance of the invoices issued in the period, not the balance as of the cut-off date.")}
        </p>
      )}

      {/* El realce del hover va como background-image y no como filter ni
          background: así se compone ENCIMA del tinte de magnitud sin pisarlo,
          respeta el box-shadow que hace de borde en la columna fija, y se ve
          igual en tema claro y oscuro. */}
      <style>{`.rubro-row:hover td {
        background-image: linear-gradient(rgba(148,163,184,0.10), rgba(148,163,184,0.10));
      }`}</style>
    </>
  );
}

/* ─── Piezas de UI ─────────────────────────────────────────────────────── */

function Card({ children, style }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 14, padding: "16px 18px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)", ...style,
    }}>{children}</div>
  );
}

function CardTitle({ icon, color, title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <span style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: `${color}16`, border: `1px solid ${color}2e`,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "0.76rem" }} />
      </span>
      <span style={{
        fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.6px",
        textTransform: "uppercase", color: "var(--text-muted)", flex: 1,
      }}>{title}</span>
      {right}
    </div>
  );
}

function KPI({ etiqueta, valor, nota, color, icono, destacado }) {
  return (
    <div style={{
      background: destacado ? tinte(color, 0.08) : "var(--bg-card)",
      border: `1px solid ${destacado ? tinte(color, 0.3) : "var(--border-card)"}`,
      borderRadius: 14, padding: "13px 15px", minWidth: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
        <i className={`fa-solid ${icono}`} style={{ color, fontSize: "0.72rem" }} />
        <span style={{
          fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.5px",
          textTransform: "uppercase", color: "var(--text-muted)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{etiqueta}</span>
      </div>
      <div style={{
        fontSize: destacado ? "1.5rem" : "1.15rem", fontWeight: 700,
        color: destacado ? color : "var(--text-primary)",
        fontVariantNumeric: "tabular-nums", lineHeight: 1.15,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{valor}</div>
      {nota && (
        <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", marginTop: 3 }}>{nota}</div>
      )}
    </div>
  );
}

function PeriodBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: "5px 13px", borderRadius: 7, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
      border: `1px solid ${active ? "#f97316" : "var(--border-card)"}`,
      background: active ? "rgba(249,115,22,0.12)" : "transparent",
      color: active ? "#f97316" : "var(--text-secondary)", transition: "all 0.15s",
    }}>{children}</button>
  );
}

function FechaInput({ label, value, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "var(--text-muted)" }}>
      {label}
      <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)}
        style={{
          background: "var(--bg)", border: "1px solid var(--border-card)", borderRadius: 7,
          color: "var(--text-secondary)", padding: "5px 9px", fontSize: "0.8rem",
        }} />
    </label>
  );
}

/* La primera columna se queda fija al desplazar en horizontal y la cabecera al
   desplazar en vertical: con muchos rubros y muchas filiales, sin esto uno
   pierde de vista de quién y de qué es la cifra que está mirando. */
function Th({ children, align = "left", first, strong, title, color, dot, onClick, orden, col }) {
  const activo = orden && orden.key === col;
  return (
    <th title={title} onClick={onClick} style={{
      textAlign: align, padding: "11px 14px", fontSize: "0.72rem", fontWeight: 700,
      letterSpacing: "0.4px", textTransform: "uppercase",
      color: activo ? "var(--text-primary)" : strong ? color : "var(--text-muted)",
      borderBottom: "1px solid var(--border-card)", whiteSpace: "nowrap",
      background: "var(--bg-card)", cursor: onClick ? "pointer" : "default",
      userSelect: "none",
      position: "sticky", top: 0, zIndex: first ? 3 : 2,
      ...(first ? { left: 0, boxShadow: "1px 0 0 var(--border-card)" } : null),
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {dot && <span style={{ width: 8, height: 8, borderRadius: 2, background: dot, flexShrink: 0 }} />}
        {children}
        {activo && (
          <i className={`fa-solid fa-caret-${orden.dir === "asc" ? "up" : "down"}`}
            style={{ fontSize: "0.7rem", opacity: 0.8 }} />
        )}
      </span>
    </th>
  );
}

function Td({ children, align = "left", first, strong, muted, color, bg, footer }) {
  return (
    <td style={{
      textAlign: align, padding: "9px 14px", fontSize: "0.84rem", whiteSpace: "nowrap",
      fontWeight: strong ? 700 : 400,
      fontVariantNumeric: "tabular-nums",
      color: color ? color : muted ? "var(--text-muted)" : "var(--text-secondary)",
      borderBottom: "1px solid var(--border-card)",
      background: footer ? "var(--bg-card)" : bg,
      ...(footer ? { position: "sticky", bottom: 0, zIndex: first ? 3 : 1,
                     borderTop: "1px solid var(--border-card)" } : null),
      ...(first ? { position: "sticky", left: 0, zIndex: footer ? 3 : 1,
                    background: bg || "var(--bg-card)",
                    boxShadow: "1px 0 0 var(--border-card)" } : null),
    }}>{children}</td>
  );
}

function Vacio({ texto, icono }) {
  return (
    <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--text-muted)" }}>
      <i className={`fa-solid ${icono}`} style={{ fontSize: "1.7rem", opacity: 0.5, display: "block", marginBottom: 10 }} />
      <span style={{ fontSize: "0.88rem" }}>{texto}</span>
    </div>
  );
}
